import React from 'react';
import InovxLoader from '@shared/global/InovxLoader';
import { PlatformSessionFailure } from './platformSession';
import '@styles/login.less';

interface ExternalLoginErrorPageProps {
    failure: PlatformSessionFailure;
    username: string;
    onContinue: (externalLogout: boolean) => void | Promise<void>;
}

function isAzureProvider(externalProviderName?: string) {
    return !externalProviderName || externalProviderName.toLowerCase() === 'aad';
}

function isUserDisabled(reason?: string) {
    return reason?.toLowerCase() === 'userdisabled';
}

const ExternalLoginErrorPage: React.FC<ExternalLoginErrorPageProps> = ({ failure, username, onContinue }) => {
    const [externalLogout, setExternalLogout] = React.useState(false);
    const displayUsername = username || InovxLoader.t('common:auth.thisAccount');
    const azureProvider = isAzureProvider(failure.ExternalProviderName);
    const providerDescription = azureProvider ? 'Azure AD/Office 365' : 'Active Directory Federation Services (ADFS)';
    const providerSignOutName = azureProvider ? 'Azure AD' : 'ADFS';
    const singleSignOutAllowed = failure.SingleSignOut === true;

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void onContinue(singleSignOutAllowed && externalLogout);
    };

    return (
        <div className="login-page-container">
            <div className="login-inner">
                <div className="login-panel panel external-login-panel">
                    <div className="external-login-header">
                        <h4 className="external-login-title">
                            <span className="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
                            {InovxLoader.t('common:auth.singleSignOnError')}
                        </h4>
                        <button
                            id="logout-close"
                            type="button"
                            className="external-login-close"
                            aria-label={InovxLoader.t('common:header.close')}
                            onClick={() => void onContinue(false)}
                        >
                            &times;
                        </button>
                    </div>

                    <form id="logoutForm" onSubmit={handleSubmit}>
                        <div className="external-login-body">
                            <div className="external-login-message" role="alert">
                                {isUserDisabled(failure.Reason) ? (
                                    <p>
                                        {InovxLoader.t('common:auth.disabledAccountPrefix', { provider: providerSignOutName })} <strong>{displayUsername}</strong> {InovxLoader.t('common:auth.disabledAccountSuffix')}
                                    </p>
                                ) : (
                                    <>
                                        <p>{InovxLoader.t('common:auth.signedInAccountIntro', { provider: providerDescription })}</p>
                                        <p><strong>{displayUsername}</strong></p>
                                        <p>{InovxLoader.t('common:auth.noVPlantAccess')}</p>
                                    </>
                                )}
                            </div>

                            {singleSignOutAllowed && (
                                <div className="checkbox logout-checkbox">
                                    <label>
                                        <input
                                            id="logout-chk"
                                            type="checkbox"
                                            checked={externalLogout}
                                            onChange={(event) => setExternalLogout(event.target.checked)}
                                        />
                                        {InovxLoader.t('common:auth.signOutFromProvider', { username: displayUsername, provider: providerSignOutName })}
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="external-login-footer">
                            <button id="logout-btn" type="submit" className="btn btn-primary external-login-ok">{InovxLoader.t('common:form.ok')}</button>
                        </div>
                    </form>
                </div>
            </div>
            <p className="inovx-copyright">{InovxLoader.t('common:about.copyright')}</p>
        </div>
    );
};

export default ExternalLoginErrorPage;
